import React, { useEffect, useState, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { TenantContext } from './TenantContext';
import { UserContext } from "./UserContext"; // Ensure correct import path
import { ConfigContext } from '../Config/ConfigContext'; // Ensure correct import path
import { AppBar, Toolbar, Typography } from '@mui/material';
import debounce from 'lodash/debounce'; // Import Lodash's debounce
import useApi from '../hooks/useApi';

/**
 * TenantProvider component that fetches tenant data and provides it via context.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - The child components that will consume the tenant context.
 * @returns {JSX.Element} The tenant context provider wrapping child components.
 */
export const TenantProvider = ({ children }) => {
	const [tenants, setTenants] = useState([]);
	const [currentTenant, setCurrentTenant] = useState(null);
	const [loading, setLoading] = useState(true);
	const [fetchError, setFetchError] = useState(false);
	const { config } = useContext(ConfigContext);
	const { user } = useContext(UserContext);
	const api = useApi();

	/**
	 * Fetch tenants and current tenant data from the API.
	 *
	 * @param {string} [tenantNameFilter=''] - Optional tenant name filter.
	 */
	const fetchTenantResults = useCallback(async (tenantNameFilter = '') => {
		try {
			// Build the API endpoint with pagination and optional tenant name filter
			const queryParams = {
				pageOffset: 0,
				pageSize: 5,
			};
			if (tenantNameFilter.trim() !== '') {
				queryParams.tenantNameFilter = tenantNameFilter.trim();
			}

			// Fetch the list of tenants using the tenant.search method from useApi with named params
			const tenantResponse = await api.tenant.search({ queryParams });
			setTenants(tenantResponse);

			// Fetch current tenant based on token using tenant.getById method with named params
			const currentTenantResponse = await api.tenant.getById({ id: user.currentTenantId });
			setCurrentTenant(currentTenantResponse);

			setFetchError(false);
		} catch (error) {
			console.error('Failed to fetch tenant data from API, using local storage if available', error);
			const storedTenants = localStorage.getItem(`${process.env.REACT_APP_APPID}_tenantData`);
			if (storedTenants) {
				setTenants(JSON.parse(storedTenants));
			} else {
				console.error('No local tenant data found');
				setFetchError(true);
			}
		} finally {
			setLoading(false);
		}
	}, []);

	// Debounced version of the fetchTenantResults function
	const debouncedFetchTenants = useCallback(
		debounce((name) => {
			fetchTenantResults(name);
		}, 300), // 300ms debounce delay; adjust as needed
		[fetchTenantResults]
	);

	// Initial fetch on mount
	useEffect(() => {
		fetchTenantResults();
		// Cleanup function to cancel any pending debounced calls on unmount
		return () => {
			debouncedFetchTenants.cancel();
		};
	}, []);

	/**
	 * Handle tenant search by name.
	 *
	 * @param {string} name - The tenant name to search for.
	 */
	const searchTenantsByName = (name) => {
		debouncedFetchTenants(name);
	};

	if (fetchError) {
		return (
			<AppBar position="static" style={{ height: '25px', backgroundColor: 'red' }}>
				<Toolbar variant="dense" style={{ minHeight: '25px' }}>
					<Typography variant="caption" color="white">
						Unable to fetch tenant data from the API
					</Typography>
				</Toolbar>
			</AppBar>
		);
	}

	if (loading) {
		return (
			<AppBar position="static" style={{ height: '25px', backgroundColor: 'green' }}>
				<Toolbar variant="dense" style={{ minHeight: '25px' }}>
					<Typography variant="caption" color="white">
						Loading tenant data...
					</Typography>
				</Toolbar>
			</AppBar>
		);
	}

	return (
		<TenantContext.Provider value={{
			currentTenant,
			setCurrentTenant,
			tenants,
			setTenants,
			reloadTenants: fetchTenantResults,
			searchTenantsByName
		}}>
			{children}
		</TenantContext.Provider>
	);
};

TenantProvider.propTypes = {
	children: PropTypes.node.isRequired,
};
